import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Typography } from '@mui/material';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { deleteRoleClaimMembership } from 'common/services/api/RoleClaimMembership/RoleClaimMembership.thunk';
import { handleDeleteDialogError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { DeleteDialog } from '../../common/components/deleteDialog/DeleteDialog';
import { setDialogOpen } from '../../common/components/deleteDialog/deleteDialogSlice';
import { DeleteDialogContentProps } from '../../common/types/TypeDefs';

export function RoleClaimMembershipDeleteDialogContent({
    title,
    itemId,
    editingRow
}: Readonly<DeleteDialogContentProps<{ claimName: string, roleName: string, claimId: string}>>) {
    const { claimName, claimId, roleName } = editingRow ?? {};
    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);
    const params = useParams();
    const { tenantId, roleId } = params;
    const dispatch = useAppDispatch();
    const pageInfo = useAppSelector(selectPageInfo);

    const successHandler = () => {
        dispatch(setDialogOpen(false));
        enqueueSnackbar('Claim link has been deleted.', { variant: 'success' });
    };

    const handleDeleteActionError = (error: { message: string; }) => {
        handleDeleteDialogError('Claim link', enqueueSnackbar, extractStatusCode(error.message));
        setProcessing(false);
    };

    const claimDeleteCallback = useCallback(() => {
        if (itemId) {
            setProcessing(true);
            dispatch(deleteRoleClaimMembership({
                tenantId: tenantId ?? '',
                roleId: roleId ?? '',
                claimId: claimId ?? ''
            }))
                .unwrap()
                .then((result) => {
                    dispatch(setRows(result.rows));
                    successHandler();
                })
                .catch((error) => {
                    handleDeleteActionError(error);
                })
                .finally(()=> {
                    setProcessing(false);
                });
        }
    }, [itemId]);

    return  (
        <DeleteDialog deleteCallback={claimDeleteCallback} processing={processing ?? false} title={`${title} link`}>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Claim Name: <Typography component="span" color="text.primary" fontWeight="400">{claimName}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Role Name: <Typography component="span" color="text.primary" fontWeight="400">{roleName}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Tenant Name: <Typography component="span" color="text.primary" fontWeight="400">{pageInfo?.tenantName}</Typography>
            </Typography>
        </DeleteDialog>
    );
}
